/** After a finalize: refetch the findings every 10 s until the queued handler lands, for at most 5 minutes. */
export const FINALIZE_POLL_MS = 10_000;
export const FINALIZE_POLL_CAP_MS = 5 * 60_000;

/**
 * A finalize request, taken before the POST. `at` (client ms) starts the poll cap; it is never compared
 * with a server stamp. `before` is the project's `finalizedAt` the findings cache held then, undefined
 * when none was loaded.
 */
export type FinalizeMarker = { at: number; before: string | null | undefined };

/**
 * The marker for a finalize the server accepted. The server's `finalizedAt`, read right after the token
 * rotation, is the baseline when the server sends it: the client cache can be stale (another admin's
 * finalize landed unseen), so the cached value is only the fallback.
 */
export const acceptedFinalizeMarker = (requested: FinalizeMarker, served: string | null | undefined): FinalizeMarker =>
  served === undefined ? requested : { ...requested, before: served };

/** Query-cache key holding the project's last successful finalize request (`FinalizeMarker`, null once landed). */
export const finalizeRequestedKey = (projectId: string) => ['finalizeRequestedAt', projectId] as const;

/**
 * The queued handler stamped the project's `finalizedAt` since the request: the stamp changed from the
 * one cached before the POST, which compares server stamps only, so a client clock behind the server
 * cannot pass an old stamp off as new. With nothing cached before the POST there is no baseline: it keeps
 * waiting until the cap rather than compare a server stamp with the client clock. Undefined (findings not loaded, or failed
 * with nothing cached) is unknown and keeps waiting; only admins finalize, and they observe the
 * findings. The pending count cannot tell this: with no correction waiting it is 0 before the handler runs.
 */
export const finalizeLanded = (finalizedAt: string | null | undefined, marker: FinalizeMarker): boolean =>
  finalizedAt != null &&
  marker.before !== undefined &&
  // Later than the baseline, not merely different: a cache still holding a stamp older than the server's
  // baseline (an intervening finalize it never saw) is not this finalize landing. Both are server stamps.
  (marker.before === null || Date.parse(finalizedAt) > Date.parse(marker.before));

/**
 * A finalize was requested within the poll cap and the queued handler has not stamped `finalizedAt`
 * since, so the finalize is still in progress. A null marker was cleared once the finalize landed.
 */
export const finalizeAwaitingRun = (
  finalizedAt: string | null | undefined,
  marker: FinalizeMarker | null | undefined,
  now: number,
): boolean => marker != null && !finalizeLanded(finalizedAt, marker) && now - marker.at < FINALIZE_POLL_CAP_MS;

/**
 * `refetchInterval` for the findings query. `POST /finalize` only queues the handler, so the first
 * refetch still reads the old finalize time; keep polling until `finalizedAt` changes.
 */
export const finalizePollInterval = (
  finalizedAt: string | null | undefined,
  marker: FinalizeMarker | null | undefined,
  now: number,
): number | false => (finalizeAwaitingRun(finalizedAt, marker, now) ? FINALIZE_POLL_MS : false);
